import { z } from 'zod';

const nonNegativeIntegerSchema = z.number().int().nonnegative();
const positiveIntegerSchema = z.number().int().positive();
const seatsSchema = z.number().int().min(2);
const identifierSchema = z.string().min(1);
const utcIsoTimestampSchema = z.string().datetime({ offset: true });

export const ticketSalesPauseReasonSchema = z.enum(['LATE_REFUND_OVER_CAPACITY']);

const ticketSalesPauseShape = {
  ticketSalesPaused: z.boolean().optional(),
  ticketSalesPauseReason: ticketSalesPauseReasonSchema.nullable().optional(),
  ticketSalesPausedAt: utcIsoTimestampSchema.nullable().optional(),
  ticketSalesPauseIncidentID: identifierSchema.nullable().optional(),
};

const validateTicketSalesPause = (
  ticket: {
    ticketSalesPaused?: boolean;
    ticketSalesPauseReason?: string | null;
    ticketSalesPausedAt?: string | null;
    ticketSalesPauseIncidentID?: string | null;
    sold: number;
    quantity: number;
    seatsSold?: number;
    seatCapacity?: number;
  },
  ctx: z.RefinementCtx,
) => {
  const paused = ticket.ticketSalesPaused === true;
  const hasPauseFacts =
    ticket.ticketSalesPauseReason != null ||
    ticket.ticketSalesPausedAt != null ||
    ticket.ticketSalesPauseIncidentID != null;
  if (
    (paused &&
      (ticket.ticketSalesPauseReason == null ||
        ticket.ticketSalesPausedAt == null ||
        ticket.ticketSalesPauseIncidentID == null)) ||
    (!paused && hasPauseFacts)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'ticket sales pause marker and incident facts must be set or cleared together',
      path: ['ticketSalesPaused'],
    });
  }
  if (
    !paused &&
    (ticket.sold > ticket.quantity ||
      (ticket.seatsSold != null &&
        ticket.seatCapacity != null &&
        ticket.seatsSold > ticket.seatCapacity))
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'unpaused inventory counters cannot exceed configured capacity',
    });
  }
};

export const groupSizeModeSchema = z.enum(['FIXED', 'MINIMUM']);
export type GroupSizeMode = z.infer<typeof groupSizeModeSchema>;

export const groupPricingModeSchema = z.enum(['FLAT', 'PER_SEAT']);
export type GroupPricingMode = z.infer<typeof groupPricingModeSchema>;

const ticketCommonShape = {
  id: identifierSchema,
  name: z
    .string()
    .min(1)
    .refine((name) => name.trim().length > 0, 'name must not be whitespace-only'),
  price: z.number().nonnegative(),
  quantity: nonNegativeIntegerSchema,
  sold: nonNegativeIntegerSchema,
  archived: z.boolean().optional(),
  ...ticketSalesPauseShape,
};

const fixedGroupTicketSchema = z
  .object({
    ...ticketCommonShape,
    type: z.literal('GROUP'),
    groupSizeMode: z.literal('FIXED'),
    pricingMode: groupPricingModeSchema,
    seats: seatsSchema,
    seatCapacity: nonNegativeIntegerSchema,
    seatsSold: nonNegativeIntegerSchema,
  })
  .passthrough()
  .superRefine((ticket, ctx) => {
    if ('minimumSeats' in ticket || 'maximumSeats' in ticket) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'FIXED Group Tickets reject minimumSeats and maximumSeats',
      });
    }
    const expectedSeatCapacity = ticket.quantity * ticket.seats;
    if (
      !Number.isSafeInteger(expectedSeatCapacity) ||
      ticket.seatCapacity !== expectedSeatCapacity
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'FIXED seatCapacity must equal quantity * seats',
        path: ['seatCapacity'],
      });
    }
    validateTicketSalesPause(ticket, ctx);
  });

const minimumGroupTicketSchema = z
  .object({
    ...ticketCommonShape,
    type: z.literal('GROUP'),
    groupSizeMode: z.literal('MINIMUM'),
    pricingMode: groupPricingModeSchema,
    minimumSeats: seatsSchema,
    maximumSeats: seatsSchema.nullable().optional(),
    seatCapacity: nonNegativeIntegerSchema,
    seatsSold: nonNegativeIntegerSchema,
  })
  .passthrough()
  .superRefine((ticket, ctx) => {
    if ('seats' in ticket) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'MINIMUM Group Tickets reject seats',
        path: ['seats'],
      });
    }
    if (ticket.maximumSeats != null && ticket.maximumSeats < ticket.minimumSeats) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'maximumSeats must be greater than or equal to minimumSeats',
        path: ['maximumSeats'],
      });
    }
    if (ticket.minimumSeats > ticket.seatCapacity) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'minimumSeats cannot exceed seatCapacity',
        path: ['minimumSeats'],
      });
    }
    if (ticket.maximumSeats != null && ticket.maximumSeats > ticket.seatCapacity) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'maximumSeats cannot exceed seatCapacity',
        path: ['maximumSeats'],
      });
    }
    validateTicketSalesPause(ticket, ctx);
  });

const individualTicketSchema = z
  .object({
    ...ticketCommonShape,
    type: z.literal('SINGLE'),
    sold: nonNegativeIntegerSchema.default(0),
  })
  .passthrough()
  .superRefine((ticket, ctx) => {
    const groupOnlyFields = [
      'groupSizeMode',
      'pricingMode',
      'minimumSeats',
      'maximumSeats',
      'seatCapacity',
      'seatsSold',
    ];
    for (const field of groupOnlyFields) {
      if (field in ticket) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `SINGLE tickets reject ${field}`,
          path: [field],
        });
      }
    }
    if ('seats' in ticket && ticket.seats !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'SINGLE compatibility seats must equal 1',
        path: ['seats'],
      });
    }
    validateTicketSalesPause(ticket, ctx);
  });

export const internalEventTicketV2Schema = z.union([
  individualTicketSchema,
  fixedGroupTicketSchema,
  minimumGroupTicketSchema,
]);
export type InternalEventTicketV2 = z.infer<typeof internalEventTicketV2Schema>;

export const internalEventConfigurationV2Schema = z
  .object({
    elementID: identifierSchema,
    elementType: z.literal('EVENT'),
    ticketContractVersion: z.literal(2),
    ticketMutationRevision: nonNegativeIntegerSchema.max(Number.MAX_SAFE_INTEGER),
    tickets: z.array(internalEventTicketV2Schema),
  })
  .passthrough()
  .superRefine((event, ctx) => {
    const ids = new Set<string>();
    for (const [index, ticket] of event.tickets.entries()) {
      if (ids.has(ticket.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'ticket IDs must be unique',
          path: ['tickets', index, 'id'],
        });
      }
      ids.add(ticket.id);
    }
  });
export type InternalEventConfigurationV2 = z.infer<typeof internalEventConfigurationV2Schema>;

const publicTicketCommonShape = {
  id: identifierSchema,
  name: z.string().min(1),
  ticketType: z.enum(['INDIVIDUAL', 'GROUP']),
  price: z.number().nonnegative(),
  quantity: nonNegativeIntegerSchema,
  sold: nonNegativeIntegerSchema,
  ...ticketSalesPauseShape,
};

const publicFixedGroupTicketSchema = z
  .object({
    ...publicTicketCommonShape,
    ticketType: z.literal('GROUP'),
    groupSizeMode: z.literal('FIXED'),
    pricingMode: groupPricingModeSchema,
    groupSize: seatsSchema,
    seatCapacity: nonNegativeIntegerSchema,
    seatsSold: nonNegativeIntegerSchema,
  })
  .passthrough()
  .superRefine((ticket, ctx) => {
    for (const field of ['minimumSeats', 'maximumSeats', 'seats']) {
      if (field in ticket) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `public FIXED Group Tickets reject ${field}`,
          path: [field],
        });
      }
    }
    const expectedSeatCapacity = ticket.quantity * ticket.groupSize;
    if (
      !Number.isSafeInteger(expectedSeatCapacity) ||
      ticket.seatCapacity !== expectedSeatCapacity
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'public FIXED inventory is inconsistent',
      });
    }
    validateTicketSalesPause(ticket, ctx);
  });

const publicMinimumGroupTicketSchema = z
  .object({
    ...publicTicketCommonShape,
    ticketType: z.literal('GROUP'),
    groupSizeMode: z.literal('MINIMUM'),
    pricingMode: groupPricingModeSchema,
    minimumSeats: seatsSchema,
    maximumSeats: seatsSchema.nullable(),
    seatCapacity: nonNegativeIntegerSchema,
    seatsSold: nonNegativeIntegerSchema,
  })
  .passthrough()
  .superRefine((ticket, ctx) => {
    for (const field of ['groupSize', 'seats']) {
      if (field in ticket) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `public MINIMUM Group Tickets reject ${field}`,
          path: [field],
        });
      }
    }
    if (ticket.maximumSeats != null && ticket.maximumSeats < ticket.minimumSeats) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'maximumSeats must be greater than or equal to minimumSeats',
        path: ['maximumSeats'],
      });
    }
    if (
      ticket.minimumSeats > ticket.seatCapacity ||
      (ticket.maximumSeats != null && ticket.maximumSeats > ticket.seatCapacity)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'public MINIMUM inventory is inconsistent',
      });
    }
    validateTicketSalesPause(ticket, ctx);
  });

const publicIndividualTicketSchema = z
  .object({
    ...publicTicketCommonShape,
    ticketType: z.literal('INDIVIDUAL'),
  })
  .passthrough()
  .superRefine((ticket, ctx) => {
    for (const field of [
      'groupSizeMode',
      'pricingMode',
      'groupSize',
      'minimumSeats',
      'maximumSeats',
      'seatCapacity',
      'seatsSold',
    ]) {
      if (field in ticket) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `public INDIVIDUAL tickets reject ${field}`,
          path: [field],
        });
      }
    }
    validateTicketSalesPause(ticket, ctx);
  });

export const publicTicketV2Schema = z.union([
  publicIndividualTicketSchema,
  publicFixedGroupTicketSchema,
  publicMinimumGroupTicketSchema,
]);
export type PublicTicketV2 = z.infer<typeof publicTicketV2Schema>;

export const purchasedGroupRequestSchema = z.object({
  purchasedGroupID: z.string().uuid(),
  ticketID: identifierSchema,
  seatCount: positiveIntegerSchema,
});
export type PurchasedGroupRequest = z.infer<typeof purchasedGroupRequestSchema>;

export const groupAttendeeRequestSchema = z
  .object({
    ticketID: identifierSchema,
    ticketName: z.string().min(1),
    type: z.literal('GROUP'),
    seats: positiveIntegerSchema,
    purchasedGroupID: z.string().uuid(),
    seatIndex: positiveIntegerSchema,
    confirmationID: identifierSchema,
  })
  .passthrough();

export const individualAttendeeRequestSchema = z
  .object({
    ticketID: identifierSchema,
    ticketName: z.string().min(1),
    type: z.literal('SINGLE'),
    confirmationID: identifierSchema,
  })
  .passthrough()
  .superRefine((ticket, ctx) => {
    for (const field of ['seats', 'purchasedGroupID', 'seatIndex']) {
      if (field in ticket) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `individual attendees reject ${field}`,
          path: [field],
        });
      }
    }
  });

export const attendeeRequestV2Schema = z.union([
  individualAttendeeRequestSchema,
  groupAttendeeRequestSchema,
]);

export const purchaseRequestV2Schema = z
  .object({
    ticketContractVersion: z.literal(2),
    purchasedGroups: z.array(purchasedGroupRequestSchema),
    tickets: z.array(attendeeRequestV2Schema),
  })
  .passthrough()
  .superRefine((request, ctx) => {
    const groupIDs = new Set<string>();
    for (const [index, group] of request.purchasedGroups.entries()) {
      if (groupIDs.has(group.purchasedGroupID)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'purchasedGroupID must be unique',
          path: ['purchasedGroups', index, 'purchasedGroupID'],
        });
      }
      groupIDs.add(group.purchasedGroupID);

      const attendees = request.tickets.filter(
        (ticket): ticket is z.infer<typeof groupAttendeeRequestSchema> =>
          ticket.type === 'GROUP' && ticket.purchasedGroupID === group.purchasedGroupID,
      );
      const indices = attendees.map((ticket) => ticket.seatIndex).sort((a, b) => a - b);
      const expected = Array.from({ length: group.seatCount }, (_, seatIndex) => seatIndex + 1);
      if (
        attendees.length !== group.seatCount ||
        attendees.some((ticket) => ticket.ticketID !== group.ticketID) ||
        attendees.some((ticket) => ticket.seats !== group.seatCount) ||
        indices.some((value, seatIndex) => value !== expected[seatIndex])
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'purchased group attendee join is incomplete or inconsistent',
          path: ['purchasedGroups', index],
        });
      }
    }

    for (const [index, attendee] of request.tickets.entries()) {
      if (attendee.type === 'GROUP' && !groupIDs.has(attendee.purchasedGroupID)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'group attendee references an unknown purchasedGroupID',
          path: ['tickets', index, 'purchasedGroupID'],
        });
      }
    }
  });
export type PurchaseRequestV2 = z.infer<typeof purchaseRequestV2Schema>;

export const persistedPurchasedGroupSchema = z
  .object({
    purchasedGroupID: z.string().uuid(),
    ticketID: identifierSchema,
    ticketName: z.string().min(1),
    seatCount: seatsSchema,
    groupSizeMode: groupSizeModeSchema,
    minimumSeats: seatsSchema.nullable().optional(),
    maximumSeats: seatsSchema.nullable().optional(),
    pricingMode: groupPricingModeSchema,
    unitPriceMinor: nonNegativeIntegerSchema,
    subtotalAmountMinor: nonNegativeIntegerSchema,
    discountAmountMinor: nonNegativeIntegerSchema,
    totalAmountMinor: nonNegativeIntegerSchema,
    currency: z.string().regex(/^[A-Z]{3}$/),
    promoCode: z.string().nullable().optional(),
    ticketInventoryReleased: z.boolean(),
    ticketInventoryReleaseOperationKeys: z
      .array(identifierSchema)
      .refine((values) => new Set(values).size === values.length),
  })
  .superRefine((group, ctx) => {
    const expectedSubtotal =
      group.pricingMode === 'FLAT' ? group.unitPriceMinor : group.unitPriceMinor * group.seatCount;
    if (
      !Number.isSafeInteger(expectedSubtotal) ||
      group.subtotalAmountMinor !== expectedSubtotal ||
      group.subtotalAmountMinor - group.discountAmountMinor !== group.totalAmountMinor
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'purchased-group minor-unit amounts are inconsistent',
      });
    }
    if (group.groupSizeMode === 'MINIMUM' && group.minimumSeats == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'MINIMUM purchased groups require minimumSeats',
        path: ['minimumSeats'],
      });
    }
    if (
      group.groupSizeMode === 'FIXED' &&
      (group.minimumSeats != null || group.maximumSeats != null)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'FIXED purchased groups reject minimumSeats and maximumSeats',
      });
    }
    if (
      group.groupSizeMode === 'MINIMUM' &&
      group.minimumSeats != null &&
      (group.seatCount < group.minimumSeats ||
        (group.maximumSeats != null && group.seatCount > group.maximumSeats))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'persisted MINIMUM group seatCount is outside its purchase-time bounds',
        path: ['seatCount'],
      });
    }
  });
export type PersistedPurchasedGroup = z.infer<typeof persistedPurchasedGroupSchema>;

export const paymentAttemptStatusSchema = z.enum([
  'PREPARED',
  'OUTCOME_UNKNOWN',
  'PROCESSOR_PENDING',
  'SUCCEEDED',
  'FAILED',
  'CANCELED',
]);

export const paymentSubmissionStateSchema = z.enum([
  'PREPARED',
  'OUTCOME_UNKNOWN',
  'PROCESSOR_PENDING',
  'TERMINAL',
]);

export const paymentAttemptSchema = z.object({
  provider: z.enum(['STRIPE', 'FINIX', 'PAYPAL', 'FREE', 'OFFLINE']),
  providerRequestKey: identifierSchema,
  providerObjectID: identifierSchema.nullable(),
  requestHash: identifierSchema,
  status: paymentAttemptStatusSchema,
  latestProviderStatus: z.string().nullable(),
  paymentSubmissionStateUpdatedAt: utcIsoTimestampSchema,
});

export const refundRequestStatusSchema = z.enum([
  'PREPARED',
  'OUTCOME_UNKNOWN',
  'PROCESSOR_PENDING',
  'SUCCEEDED',
  'FAILED',
  'CANCELED',
]);

export const refundRequestSchema = z.object({
  refundRequestID: z.string().uuid(),
  requestHash: identifierSchema,
  amountMinor: positiveIntegerSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  confirmationIDs: z
    .array(identifierSchema)
    .refine((values) => new Set(values).size === values.length),
  purchasedGroupIDs: z
    .array(z.string().uuid())
    .refine((values) => new Set(values).size === values.length),
  provider: z.enum(['STRIPE', 'FINIX', 'PAYPAL']),
  providerRequestKey: identifierSchema,
  providerRefundID: identifierSchema.nullable(),
  status: refundRequestStatusSchema,
  refundStatusUpdatedAt: utcIsoTimestampSchema,
});

const ticketReleaseReasonSchema = z.string().regex(/^(PAYMENT|REFUND|NONFINANCIAL):[^:]+$/);

const persistedIndividualAttendeeSchema = z
  .object({
    ticketID: identifierSchema,
    ticketName: z.string().min(1),
    type: z.literal('SINGLE'),
    confirmationID: identifierSchema,
    pendingRefundRequestID: z.string().uuid().nullable(),
    ticketInventoryReleaseReasons: z.array(ticketReleaseReasonSchema),
  })
  .superRefine((attendee, ctx) => {
    const value = attendee as typeof attendee & Record<string, unknown>;
    for (const field of ['seats', 'purchasedGroupID', 'seatIndex']) {
      if (field in value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `persisted individual attendees reject ${field}`,
          path: [field],
        });
      }
    }
    if (
      new Set(attendee.ticketInventoryReleaseReasons).size !==
      attendee.ticketInventoryReleaseReasons.length
    ) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'release reasons must be unique' });
    }
  });

const persistedGroupAttendeeSchema = z
  .object({
    ticketID: identifierSchema,
    ticketName: z.string().min(1),
    type: z.literal('GROUP'),
    seats: seatsSchema,
    purchasedGroupID: z.string().uuid(),
    seatIndex: positiveIntegerSchema,
    confirmationID: identifierSchema,
    pendingRefundRequestID: z.string().uuid().nullable(),
    ticketInventoryReleaseReasons: z.array(ticketReleaseReasonSchema),
  })
  .superRefine((attendee, ctx) => {
    if (
      new Set(attendee.ticketInventoryReleaseReasons).size !==
      attendee.ticketInventoryReleaseReasons.length
    ) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'release reasons must be unique' });
    }
  });

export const persistedTicketAttendeeSchema = z.union([
  persistedIndividualAttendeeSchema,
  persistedGroupAttendeeSchema,
]);

export const ticketInventoryHistoryEntrySchema = z
  .object({
    operationKey: identifierSchema,
    operationType: z.enum(['RESERVATION', 'RELEASE', 'REACQUIRE']),
    releaseRequestHash: identifierSchema.optional(),
    confirmationIDs: z
      .array(identifierSchema)
      .refine((values) => new Set(values).size === values.length),
    purchasedGroupIDs: z
      .array(z.string().uuid())
      .refine((values) => new Set(values).size === values.length),
    groupUnitsDelta: z.number().int(),
    seatsDelta: z.number().int(),
    occurredAt: utcIsoTimestampSchema,
  })
  .superRefine((entry, ctx) => {
    if (entry.operationType === 'RELEASE' && !entry.releaseRequestHash) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['releaseRequestHash'],
        message: 'releaseRequestHash is required for RELEASE history',
      });
    }
  });

export const ticketInventoryRemediationSchema = z.object({
  reason: z.enum(['PAYMENT_CONTRADICTION', 'REFUND_TERMINAL_CORRECTION', 'OTHER']),
  provider: z.enum(['STRIPE', 'FINIX', 'PAYPAL']),
  providerObjectID: identifierSchema,
  refundRequestID: z.string().uuid().nullable(),
  verifiedAt: utcIsoTimestampSchema,
  confirmationIDs: z
    .array(identifierSchema)
    .refine((values) => new Set(values).size === values.length),
  purchasedGroupIDs: z
    .array(z.string().uuid())
    .refine((values) => new Set(values).size === values.length),
  affectedTickets: z
    .array(
      z.object({
        ticketID: identifierSchema,
        configuredQuantity: nonNegativeIntegerSchema,
        groupObligations: nonNegativeIntegerSchema,
        groupDeficit: nonNegativeIntegerSchema,
        configuredSeatCapacity: nonNegativeIntegerSchema,
        seatObligations: nonNegativeIntegerSchema,
        seatDeficit: nonNegativeIntegerSchema,
        paused: z.boolean(),
      }),
    )
    .refine((tickets) => new Set(tickets.map(({ ticketID }) => ticketID)).size === tickets.length),
});

export const persistedTicketOperationLedgerSchema = z
  .object({
    transactionID: identifierSchema,
    ticketContractVersion: z.literal(2),
    totalChargedAmountMinor: nonNegativeIntegerSchema,
    currency: z.string().regex(/^[A-Z]{3}$/),
    purchasedGroups: z.array(persistedPurchasedGroupSchema),
    tickets: z.array(persistedTicketAttendeeSchema),
    inventoryReservationHash: identifierSchema,
    ticketInventoryRevision: nonNegativeIntegerSchema,
    ticketInventoryStatus: z.enum([
      'ACTIVE',
      'PARTIALLY_RELEASED',
      'RELEASED',
      'OVER_CAPACITY',
      'NEEDS_REMEDIATION',
    ]),
    paymentSubmissionState: paymentSubmissionStateSchema,
    paymentAttempt: paymentAttemptSchema,
    refundRevision: nonNegativeIntegerSchema,
    refundPendingAmountMinor: nonNegativeIntegerSchema,
    refundSucceededAmountMinor: nonNegativeIntegerSchema,
    refundRequests: z.array(refundRequestSchema),
    appliedTicketInventoryOperationKeys: z.array(identifierSchema),
    providerDeliveryKeys: z.array(identifierSchema),
    providerObservationKeys: z.array(identifierSchema),
    ticketInventoryHistory: z.array(ticketInventoryHistoryEntrySchema),
    remediationRequiredAt: utcIsoTimestampSchema.nullable().optional(),
    ticketInventoryRemediation: ticketInventoryRemediationSchema.nullable(),
  })
  .passthrough()
  .superRefine((ledger, ctx) => {
    const expectedSubmissionState =
      ledger.paymentAttempt.status === 'SUCCEEDED' ||
      ledger.paymentAttempt.status === 'FAILED' ||
      ledger.paymentAttempt.status === 'CANCELED'
        ? 'TERMINAL'
        : ledger.paymentAttempt.status;
    if (ledger.paymentSubmissionState !== expectedSubmissionState) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'paymentAttempt.status does not map to paymentSubmissionState',
        path: ['paymentSubmissionState'],
      });
    }
    if (
      (ledger.ticketInventoryStatus === 'OVER_CAPACITY' ||
        ledger.ticketInventoryStatus === 'NEEDS_REMEDIATION') &&
      (ledger.remediationRequiredAt == null || ledger.ticketInventoryRemediation == null)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'remediationRequiredAt is required for remediation states',
        path: ['remediationRequiredAt'],
      });
    }
    if (
      ledger.ticketInventoryStatus !== 'OVER_CAPACITY' &&
      ledger.ticketInventoryStatus !== 'NEEDS_REMEDIATION' &&
      ledger.ticketInventoryRemediation != null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'ticketInventoryRemediation is only valid for remediation states',
        path: ['ticketInventoryRemediation'],
      });
    }

    const requireUnique = (values: string[], path: string) => {
      if (new Set(values).size !== values.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${path} must contain unique values`,
          path: [path],
        });
      }
    };
    requireUnique(
      ledger.appliedTicketInventoryOperationKeys,
      'appliedTicketInventoryOperationKeys',
    );
    requireUnique(ledger.providerDeliveryKeys, 'providerDeliveryKeys');
    requireUnique(ledger.providerObservationKeys, 'providerObservationKeys');
    requireUnique(
      ledger.refundRequests.map(({ refundRequestID }) => refundRequestID),
      'refundRequests',
    );
    requireUnique(
      ledger.tickets.map(({ confirmationID }) => confirmationID),
      'tickets',
    );
    requireUnique(
      ledger.purchasedGroups.map(({ purchasedGroupID }) => purchasedGroupID),
      'purchasedGroups',
    );
    requireUnique(
      ledger.ticketInventoryHistory.map(({ operationKey }) => operationKey),
      'ticketInventoryHistory',
    );

    const pendingStatuses = new Set(['PREPARED', 'OUTCOME_UNKNOWN', 'PROCESSOR_PENDING']);
    const refundByID = new Map(
      ledger.refundRequests.map((request) => [request.refundRequestID, request]),
    );
    const confirmationIDs = new Set(ledger.tickets.map(({ confirmationID }) => confirmationID));
    const purchasedGroupIDs = new Set(
      ledger.purchasedGroups.map(({ purchasedGroupID }) => purchasedGroupID),
    );
    for (const [index, refund] of ledger.refundRequests.entries()) {
      if (
        refund.confirmationIDs.some((confirmationID) => !confirmationIDs.has(confirmationID)) ||
        refund.purchasedGroupIDs.some(
          (purchasedGroupID) => !purchasedGroupIDs.has(purchasedGroupID),
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'refund selections must join to persisted attendees and purchased groups',
          path: ['refundRequests', index],
        });
      }
    }
    for (const [index, attendee] of ledger.tickets.entries()) {
      if (attendee.pendingRefundRequestID != null) {
        const claim = refundByID.get(attendee.pendingRefundRequestID);
        if (
          claim == null ||
          !pendingStatuses.has(claim.status) ||
          !claim.confirmationIDs.includes(attendee.confirmationID)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'pending attendee claim must reference a pending refund request',
            path: ['tickets', index, 'pendingRefundRequestID'],
          });
        }
      }
      for (const reason of attendee.ticketInventoryReleaseReasons) {
        if (reason.startsWith('REFUND:')) {
          const refund = refundByID.get(reason.slice('REFUND:'.length));
          if (
            refund?.status !== 'SUCCEEDED' ||
            !refund.confirmationIDs.includes(attendee.confirmationID)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'refund release reason must reference a successful refund for that attendee',
              path: ['tickets', index, 'ticketInventoryReleaseReasons'],
            });
          }
        }
      }
    }

    for (const [index, group] of ledger.purchasedGroups.entries()) {
      const attendees = ledger.tickets.filter(
        (attendee): attendee is z.infer<typeof persistedGroupAttendeeSchema> =>
          attendee.type === 'GROUP' && attendee.purchasedGroupID === group.purchasedGroupID,
      );
      const indices = attendees.map(({ seatIndex }) => seatIndex).sort((a, b) => a - b);
      const expectedIndices = Array.from(
        { length: group.seatCount },
        (_, seatIndex) => seatIndex + 1,
      );
      const allReleased =
        attendees.length > 0 &&
        attendees.every(
          ({ ticketInventoryReleaseReasons }) => ticketInventoryReleaseReasons.length > 0,
        );
      if (
        attendees.length !== group.seatCount ||
        attendees.some(
          ({ ticketID, seats }) => ticketID !== group.ticketID || seats !== group.seatCount,
        ) ||
        indices.some((value, seatIndex) => value !== expectedIndices[seatIndex]) ||
        group.ticketInventoryReleased !== allReleased ||
        (group.ticketInventoryReleased && group.ticketInventoryReleaseOperationKeys.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'persisted purchased-group attendee join or release marker is inconsistent',
          path: ['purchasedGroups', index],
        });
      }
      if (
        group.ticketInventoryReleaseOperationKeys.some(
          (operationKey) => !ledger.appliedTicketInventoryOperationKeys.includes(operationKey),
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'group release operation keys must be present in the applied operation set',
          path: ['purchasedGroups', index, 'ticketInventoryReleaseOperationKeys'],
        });
      }
    }

    const remediation = ledger.ticketInventoryRemediation;
    if (remediation != null) {
      for (const [index, ticket] of remediation.affectedTickets.entries()) {
        const expectedGroupDeficit = Math.max(
          0,
          ticket.groupObligations - ticket.configuredQuantity,
        );
        const expectedSeatDeficit = Math.max(
          0,
          ticket.seatObligations - ticket.configuredSeatCapacity,
        );
        if (
          ticket.groupDeficit !== expectedGroupDeficit ||
          ticket.seatDeficit !== expectedSeatDeficit ||
          ticket.paused !== (expectedGroupDeficit > 0 || expectedSeatDeficit > 0)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'remediation deficits and paused state must match capacity obligations',
            path: ['ticketInventoryRemediation', 'affectedTickets', index],
          });
        }
      }

      if (remediation.reason === 'REFUND_TERMINAL_CORRECTION') {
        const refund = remediation.refundRequestID
          ? refundByID.get(remediation.refundRequestID)
          : undefined;
        const reacquireOperationKey = remediation.refundRequestID
          ? `REFUND_REACQUIRE:${remediation.refundRequestID}`
          : '';
        const history = ledger.ticketInventoryHistory.find(
          ({ operationKey }) => operationKey === reacquireOperationKey,
        );
        const sameSet = (left: string[], right: string[]) =>
          left.length === right.length && left.every((value) => right.includes(value));
        const releaseReason = remediation.refundRequestID
          ? `REFUND:${remediation.refundRequestID}`
          : '';
        const correctedAttendees = ledger.tickets.filter(({ confirmationID }) =>
          remediation.confirmationIDs.includes(confirmationID),
        );
        if (
          refund == null ||
          (refund.status !== 'FAILED' && refund.status !== 'CANCELED') ||
          refund.provider !== remediation.provider ||
          refund.providerRefundID !== remediation.providerObjectID ||
          !sameSet(refund.confirmationIDs, remediation.confirmationIDs) ||
          !sameSet(refund.purchasedGroupIDs, remediation.purchasedGroupIDs) ||
          correctedAttendees.length !== remediation.confirmationIDs.length ||
          correctedAttendees.some(({ ticketInventoryReleaseReasons }) =>
            ticketInventoryReleaseReasons.includes(releaseReason),
          ) ||
          !ledger.appliedTicketInventoryOperationKeys.includes(reacquireOperationKey) ||
          history?.operationType !== 'REACQUIRE' ||
          !sameSet(history.confirmationIDs, remediation.confirmationIDs) ||
          !sameSet(history.purchasedGroupIDs, remediation.purchasedGroupIDs) ||
          !remediation.affectedTickets.some(({ groupDeficit, seatDeficit }) =>
            Boolean(groupDeficit || seatDeficit),
          )
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'late-refund remediation must prove the verified reacquisition transition',
            path: ['ticketInventoryRemediation'],
          });
        }
      }
    }

    const releasedAttendeeCount = ledger.tickets.filter(
      ({ ticketInventoryReleaseReasons }) => ticketInventoryReleaseReasons.length > 0,
    ).length;
    const expectedInventoryStatus =
      releasedAttendeeCount === 0
        ? 'ACTIVE'
        : releasedAttendeeCount === ledger.tickets.length
          ? 'RELEASED'
          : 'PARTIALLY_RELEASED';
    if (
      !['OVER_CAPACITY', 'NEEDS_REMEDIATION'].includes(ledger.ticketInventoryStatus) &&
      ledger.ticketInventoryStatus !== expectedInventoryStatus
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'ticketInventoryStatus does not match attendee release state',
        path: ['ticketInventoryStatus'],
      });
    }
    const expectedPendingAmount = ledger.refundRequests
      .filter((request) => pendingStatuses.has(request.status))
      .reduce((sum, request) => sum + request.amountMinor, 0);
    const expectedSucceededAmount = ledger.refundRequests
      .filter((request) => request.status === 'SUCCEEDED')
      .reduce((sum, request) => sum + request.amountMinor, 0);
    if (
      ledger.refundPendingAmountMinor !== expectedPendingAmount ||
      ledger.refundSucceededAmountMinor !== expectedSucceededAmount ||
      ledger.refundPendingAmountMinor + ledger.refundSucceededAmountMinor >
        ledger.totalChargedAmountMinor ||
      ledger.refundRequests.some(({ currency }) => currency !== ledger.currency) ||
      ledger.purchasedGroups.some(({ currency }) => currency !== ledger.currency)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'refund amount counters do not match refundRequests',
        path: ['refundRequests'],
      });
    }
    const historyKeys = new Set(
      ledger.ticketInventoryHistory.map(({ operationKey }) => operationKey),
    );
    if (
      ledger.appliedTicketInventoryOperationKeys.some(
        (operationKey) => !historyKeys.has(operationKey),
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'every applied inventory operation requires a history entry',
        path: ['ticketInventoryHistory'],
      });
    }
  });
export type PersistedTicketOperationLedger = z.infer<typeof persistedTicketOperationLedgerSchema>;

export const groupTicketErrorCodeSchema = z.enum([
  'STALE_TICKET_CONTRACT',
  'GROUP_INVENTORY_UNAVAILABLE',
  'SEAT_INVENTORY_UNAVAILABLE',
  'INVALID_PURCHASED_GROUP',
  'IDEMPOTENCY_CONFLICT',
]);

export const groupTicketErrorSchema = z
  .object({
    statusCode: z.union([z.literal(409), z.literal(422)]),
    code: groupTicketErrorCodeSchema,
    message: z.string().min(1),
  })
  .superRefine((error, ctx) => {
    const expectedStatus = error.code === 'INVALID_PURCHASED_GROUP' ? 422 : 409;
    if (error.statusCode !== expectedStatus) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${error.code} must use HTTP ${expectedStatus}`,
        path: ['statusCode'],
      });
    }
  });
export type GroupTicketError = z.infer<typeof groupTicketErrorSchema>;
